import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import type { Place } from '../../types';
import { getPlaceById } from '../../services/placesService';
import { PlaceList } from '../places/PlaceList';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface FavoritesListProps {
  favoriteIds: string[];
  onSelectPlace: (place: Place) => void;
  onToggleFavorite: (placeId: string) => void;
}

export function FavoritesList({
  favoriteIds,
  onSelectPlace,
  onToggleFavorite,
}: FavoritesListProps) {
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadPlaces() {
      if (favoriteIds.length === 0) {
        setPlaces([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const loadedPlaces = await Promise.all(
          favoriteIds.map((id) => getPlaceById(id))
        );
        setPlaces(loadedPlaces.filter((p): p is Place => p !== null));
      } catch (error) {
        console.error('Error loading favorites:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadPlaces();
  }, [favoriteIds]);

  if (isLoading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Heart className="w-6 h-6 text-red-500 fill-red-500" />
        <h2 className="text-xl font-bold text-gray-800">
          Meine Lieblingsorte ({places.length})
        </h2>
      </div>

      <PlaceList
        places={places}
        favorites={favoriteIds}
        isLoading={false}
        emptyType="favorites"
        onSelectPlace={onSelectPlace}
        onToggleFavorite={onToggleFavorite}
      />
    </div>
  );
}
